<script>import { formReset } from "../../actions/formReset.js";
import * as d3 from "d3";
import DateTimeField from "./DateTimeField.svelte";
import { createEventDispatcher, getContext } from "svelte";
import {
  createFieldContext,
  mergeStateEventDetails
} from "./helper/field.js";
import YearPicker from "./helper/YearPicker.svelte";
import QuarterPicker from "./helper/QuarterPicker.svelte";
import MonthPicker from "./helper/MonthPicker.svelte";
import {
  WeekNumberSystem,
  getFirstWeekDay,
  getMonth,
  getQuarter,
  getWeekInterval,
  getWeekOther,
  getWeekStartOther
} from "../../util/date-time-util.js";
import DatePicker from "./helper/DatePicker.svelte";
import FieldContainer from "./helper/FieldContainer.svelte";
import WeekPicker from "./helper/WeekPicker.svelte";
import SelectField from "./SelectField.svelte";
import { getDictionaryLocale } from "../../util/i18n-util.js";
import { capitalize } from "../../util/string-util.js";
import { Locale, dictionaries, lookupMessage } from "../../i18n/index.js";
export let name = void 0;
export let label;
export let callToInput = void 0;
export let assistText = void 0;
export let story = "filled.dateTime";
export let value = void 0;
export let error = void 0;
export let disabled = false;
export let format = void 0;
export let fixedType = void 0;
export let precision = void 0;
export let system = WeekNumberSystem.ISO8601;
export let control = void 0;
export let locale = void 0;
export const context = void 0;
const dispatch = createEventDispatcher();
const contextKey = createFieldContext(name, value, error, disabled, value === void 0);
const ctx = getContext(contextKey);
const valueInMillis = value !== void 0 && value.start.toPrecision;
const valueFormat = value !== void 0 && value.start.toPrecision ? "epoch" : "iso";
let type = value !== void 0 ? value.type : control ? control.input.properties?.type ?? "custom" : "custom";
fixedType = fixedType !== void 0 ? fixedType : control ? control.input.properties?.fixedType ?? true : true;
precision = precision !== void 0 ? precision : control ? control.input.properties?.precision : void 0;
format = format !== void 0 ? format : control ? control.input.properties?.format ?? valueFormat : valueFormat;
const date = true;
const time = precision !== void 0;
$:
  dictionaryLocale = getDictionaryLocale(locale);
$:
  dictionary = dictionaries[dictionaryLocale];
$:
  typeOptions = [
    { value: "year", label: capitalize(lookupMessage("temporalUnits.years.long.singular", dictionary) ?? "year") },
    { value: "quarter", label: capitalize(lookupMessage("temporalUnits.quarters.long.singular", dictionary) ?? "quarter") },
    { value: "month", label: capitalize(lookupMessage("temporalUnits.months.long.singular", dictionary) ?? "month") },
    { value: "week", label: capitalize(lookupMessage("temporalUnits.weeks.long.singular", dictionary) ?? "week") },
    { value: "day", label: capitalize(lookupMessage("temporalUnits.days.long.singular", dictionary) ?? "day") },
    { value: "custom", label: capitalize(lookupMessage("custom", dictionary) ?? "custom") }
  ];
let labels = label.split(":");
let callToInputs = callToInput ? callToInput.split(":") : [void 0, void 0];
let assistTexts = assistText ? assistText.split(":") : [void 0, void 0];
let errors = error ? error.split(":") : [void 0, void 0];
$:
  startAsDate = value && value.start !== void 0 ? new Date(value.start) : /* @__PURE__ */ new Date();
$:
  start = value?.start;
$:
  end = value?.end;
const startChanged = (ev) => {
  start = ev.detail.value;
  updateValue();
};
const endChanged = (ev) => {
  end = ev.detail.value;
  updateValue();
};
const startStateChanged = (ev) => {
  startStateEventDetails = ev.detail;
  updateState();
};
const endStateChanged = (ev) => {
  endStateEventDetails = ev.detail;
  updateState();
};
const updateValue = () => {
  if (start !== void 0 && end !== void 0) {
    const startMillis = start.toPrecision ? start : start.getTime();
    const endMillis = end.toPrecision ? end : end.getTime();
    const startValue = valueInMillis ? Math.min(startMillis, endMillis) : new Date(Math.min(startMillis, endMillis));
    const endValue = valueInMillis ? Math.max(startMillis, endMillis) : new Date(Math.max(startMillis, endMillis));
    value = { start: startValue, end: endValue, type };
  } else {
    value = void 0;
  }
  dispatch("changed", {
    name,
    value
  });
};
const updateState = () => {
  stateEventDetails = mergeStateEventDetails(startStateEventDetails, endStateEventDetails);
  if (stateEventDetails.state.focus === false) {
    if (start !== void 0 && end !== void 0 && start > end) {
      const park = start;
      start = end;
      end = park;
    }
  }
  if (Object.keys(stateEventDetails.changes).length > 0) {
    dispatch("state", stateEventDetails);
  }
};
let startStateEventDetails;
let endStateEventDetails;
let stateEventDetails;
const getRange = (date2, type2) => {
  let start2 = date2;
  let end2 = date2;
  switch (type2) {
    case "year":
      start2 = d3.timeYear.floor(date2);
      end2 = d3.timeYear.offset(start2, 1);
      break;
    case "quarter":
      start2 = d3.timeMonth.every(3).floor(date2);
      end2 = d3.timeMonth.every(3).offset(start2, 1);
      break;
    case "month":
      start2 = d3.timeMonth.floor(date2);
      end2 = d3.timeMonth.offset(start2, 1);
      break;
    case "week":
      start2 = getWeekInterval(getFirstWeekDay(system)).floor(date2);
      end2 = getWeekInterval(getFirstWeekDay(system)).offset(start2, 1);
      break;
    case "day":
      start2 = d3.timeDay.floor(date2);
      end2 = d3.timeDay.offset(start2, 1);
      break;
  }
  return valueInMillis ? { start: start2.getTime(), end: end2.getTime(), type: type2 } : { start: start2, end: end2, type: type2 };
};
const onTimeRangeChanged = () => {
  value = getRange(startAsDate, type);
  dispatch("changed", {
    name,
    value
  });
};
$: {
  if (type !== "custom") {
    onTimeRangeChanged();
  }
}
$:
  props = {
    //		label: typeOptions ? typeOptions.filter(option => option.value === type)[0].label : '' ,
    label: typeOptions.filter((option) => option.value === type)[0].label,
    callToInput: void 0,
    assistText: void 0,
    story,
    contextKey
  };
</script>

{#if !fixedType}
	<SelectField
		label={capitalize(lookupMessage('period', dictionary) ?? 'period')}
		{story}
		bind:value={type}
		options={typeOptions}
	/>
{/if}
<input name={name ? name : ''} use:formReset type="hidden" bind:value />
{#if type === 'custom'}
	<DateTimeField
		value={start}
		{locale}
		{disabled}
		{format}
		{story}
		error={errors[0]}
		label={labels[0]}
		callToInput={callToInputs[0]}
		assistText={assistTexts[0]}
		on:changed={startChanged}
		on:state={startStateChanged}
	/>
	<DateTimeField
		value={end}
		{locale}
		{disabled}
		{format}
		{story}
		error={errors[1]}
		label={labels[1]}
		callToInput={callToInputs[1]}
		assistText={assistTexts[1]}
		on:changed={endChanged}
		on:state={endStateChanged}
	/>
{:else}
	<FieldContainer {...props}>
		<svelte:fragment slot="input">
			{#if type === 'year' || type === 'quarter' || type === 'month' || type === 'week'}
				<YearPicker
					value={startAsDate.getFullYear()}
					on:changed={(ev) => {
						if (ev.detail.value !== undefined) {
							startAsDate.setFullYear(ev.detail.value);
							onTimeRangeChanged();
						}
					}}
					on:action
				/>
			{/if}
			{#if type === 'quarter'}
				<QuarterPicker
					value={getQuarter(startAsDate)}
					on:changed={(ev) => {
						if (ev.detail.value !== undefined) {
							startAsDate.setMonth(ev.detail.value);
							onTimeRangeChanged();
						}
					}}
					on:action
				/>
			{/if}
			{#if type === 'month'}
				<MonthPicker
					value={getMonth(startAsDate)}
					{system}
					{locale}
					on:changed={(ev) => {
						if (ev.detail.value !== undefined) {
							startAsDate.setMonth(ev.detail.value);
							onTimeRangeChanged();
						}
					}}
					on:action
				/>
			{/if}
			{#if type === 'week'}
				<WeekPicker
					value={getWeekOther(system, startAsDate)}
					{system}
					on:changed={(ev) => {
						if (ev.detail.value !== undefined) {
							startAsDate = getWeekStartOther(system, ev.detail.value);
							onTimeRangeChanged();
						}
					}}
					on:action
				/>
			{/if}
			{#if type === 'day'}
				<DatePicker
					value={[startAsDate, undefined]}
					{system}
					{locale}
					pick={'start'}
					on:changed={(ev) => {
						if (ev.detail.value !== undefined) {
							startAsDate = ev.detail.value[0];
							onTimeRangeChanged();
						}
					}}
					on:action
				/>
			{/if}
		</svelte:fragment>
	</FieldContainer>
{/if}
